import { useState, useEffect } from 'react';
import { useMediaQuery } from '@mui/material';
import DOMPurify from 'dompurify';
import Autocomplete from '@mui/material/Autocomplete';
import TeamBox from '@ManagementRepresentationalDisplay/TeamBox';
import UserListRow from '@ManagementRepresentationalDisplay/UserListRow';
import { BsPlusLg as Plus } from 'react-icons/bs';
import { MdEdit as Edit } from 'react-icons/md';
import { MdGroups as Teams, MdPeople as People, MdSettings as Settings } from 'react-icons/md';
import { BsCheckSquare as Check } from 'react-icons/bs';
import { BsXSquare as Close } from 'react-icons/bs';
import { BsArrowRight as Goto } from 'react-icons/bs';
import { ReactComponent as ProfilePicture } from '@Assets/blankProfile.svg';
import TextField from '@mui/material/TextField';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import AddUsersView from '@CommonViews/AddUsersView';
import AddTeamForm from '@CommonForms/AddTeamForm';
import BorderBlueButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderBlueButtonV2';
import BorderRedButtonV2 from '@CommonRepresentationalDisplay/Buttons/BorderRedButtonV2';
import {Tabs, Tab} from '@mui/material';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import DeleteTeamForm from '@OrganizationForms/DeleteTeamForm';
import TeamsList from '@OrganizationPages/TeamsList';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import TeamService from '@Helpers/URLHelpers/TeamService';
import ProjectService from '@Helpers/URLHelpers/ProjectService';
import ErrorHandler from '@Root/ErrorHandler';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import './TeamsPage.scss';

export default function TeamsPage(props) {

    const [teams, setTeams] = useState([]);
    const [cursor, setCursor] = useState('auto');
    const [teamPosition, setTeamPosition] = useState(0);
    const [selectedTeam, setSelectedTeam] = useState({});
    const [permissions, setPermissions] = useState({});
    const [showAddTeamForm, setShowAddTeamForm] = useState(false);
    const [showAddUsersView, setShowAddUsersView] = useState(false);
    const [showDeleteTeamForm, setShowDeleteTeamForm] = useState(false);
    const [teamUsers, setTeamUsers] = useState([]);
    const [pageLoader, setPageLoader] = useState(true);
    const [teamTabPosition, setTeamTabPosition] = useState(0);
    const [teamTitleEdit, setTeamTitleEdit] = useState(false);
    const [teamTagEdit, setTeamTagEdit] = useState(false);
    const [ownerEdit, setOwnerEdit] = useState(false);
    const [teamTag, setTeamTag] = useState('')
    const [teamTitle, setTeamTitle] = useState('')
    const [ownerUser, setOwnerUser] = useState({});
    const [ownerUserBackup, setOwnerUserBackup] = useState({});
    const [orgUsers, setOrgUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [teamMemberCounts, setTeamMemberCounts] = useState({});
    const [contentLoader, setContentLoader] = useState(false);
    const teamService = new TeamService();
    const errorHandler = new ErrorHandler();
    const userAuthService = new UserAuthService();
    const userInfoService = new UserInfoService();
    const { activeUserId, activeUserAccessList } = useReduxActiveUser();
    const { currentOrg, currentOrgId, currentUserOrgPermissions } = useReduxOrg();
    const is0To991 = useMediaQuery('(max-width: 991px)');
    const is0To768 = useMediaQuery('(max-width: 768px)');
    const className = 'organization__teamsPage';

    useEffect(() => {
        renderData();
    }, [])

    const fetchTeamMemberCounts = async (teams) => {
        const counts = {};
        for (const team of teams) {
            try {
                let userList = await teamService.getUserListByTeamId(team.id);
                userList = JSON.parse(userList.body);
                counts[team.id] = userList.length;
            } catch (err) {
                counts[team.id] = 0;
            }
        }
        setTeamMemberCounts(counts);
    };

    const renderData = async () => {
        setCursor('wait');
        try {
            let teamsObj = await teamService.getTeamListByOrgId(currentOrgId);
            teamsObj = JSON.parse(teamsObj.body);
            let userPermissions = await userAuthService.getUserPermissionForParent(activeUserId, 'org', currentOrgId);
            userPermissions = JSON.parse(userPermissions.body);
            let userList = await userInfoService.getUserListByOrgId(currentOrgId);
            userList = JSON.parse(userList.body);
            setOrgUsers(userList);
            if(!userPermissions.allowAdmin) {
                let accessList = activeUserAccessList;
                if(Object.keys(accessList).length == 0) {
                    let accessList = await userAuthService.getAccessListForUser(activeUserId);
                    accessList = JSON.parse(accessList.body);
                }
                teamsObj = teamsObj.filter(team => {
                    return accessList.teams.some(item => JSON.parse(item.id) === team.id)
                })
            }
            setTeams(teamsObj);
            // Fetch member counts for all teams
            await fetchTeamMemberCounts(teamsObj);
            if(teamsObj.length > 0) await showTeamsDetails(teamsObj[0]);
        } catch (err) {
            errorHandler.handleError(err);
        }
        setPageLoader(false);
        setCursor('auto');
    }

    const reloadData = () => {
        setPageLoader(true);
        setTeamPosition(0);
        renderData();
    }

    const toggleAddUsersView = () => {
        setShowAddUsersView(!showAddUsersView);
    }

    const toggleAddTeamForm = () => {
        setShowAddTeamForm(!showAddTeamForm);
    }

    const handleDeleteClick = () => {
        setShowDeleteTeamForm(!showDeleteTeamForm);
    }

    const filteredTeams = teams.filter(team => 
        team.teamName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        team.tag.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const populateTeams = () => {
        return filteredTeams.map((team, index) => {
            const memberCount = teamMemberCounts[team.id] ?? 0;

            return (
                <div 
                    key={team.id}
                    className={`${className}__teamCard ${selectedTeam?.id === team.id ? 'active' : ''}`}
                    onClick={() => {
                        const actualIndex = teams.findIndex(t => t.id === team.id);
                        handleTeamTabClick(null, actualIndex);
                    }}>
                    {/* Team Icon/Avatar */}
                    <div className={`${className}__teamCard__avatar`}>
                        <Teams className={`${className}__teamCard__avatar__icon`} />
                    </div>

                    {/* Team Info */}
                    <div className={`${className}__teamCard__info`}>
                        <div className={`${className}__teamCard__tag`}>{team.tag}</div>
                        <div className={`${className}__teamCard__name`}>{team.teamName}</div>
                        <div className={`${className}__teamCard__meta`}>
                            <People className={`${className}__teamCard__meta__icon`} />
                            {memberCount} {memberCount === 1 ? 'member' : 'members'}
                        </div>
                    </div>

                    {/* Badges */}
                    <div className={`${className}__teamCard__badges`}>
                        {team.ownerUserId === JSON.parse(activeUserId) && (
                            <div className={`${className}__teamCard__badge owner`}>
                                <Settings />
                                Owned by you
                            </div>
                        )}
                    </div>

                    {/* Footer with Arrow */}
                    <div className={`${className}__teamCard__footer`}>
                        <Goto className={`${className}__teamCard__icon`} />
                    </div>
                </div>
            );
        });
    }

    const showTeamsDetails = async(team) => {
        try {
            setContentLoader(true);
            setSelectedTeam(team);
            let userList = await teamService.getUserListByTeamId(team.id);
            userList = JSON.parse(userList.body);
            let userPermissions = await userAuthService.getUserPermissionForParent(activeUserId, 'team', team.id);
            userPermissions = JSON.parse(userPermissions.body);
            setPermissions(userPermissions);
            setTeamUsers(userList);
            let ownerUserObj = await userInfoService.getUserById(team.ownerUserId);
            ownerUserObj = JSON.parse(ownerUserObj.body);
            setOwnerUser(ownerUserObj);
            setOwnerUserBackup(ownerUserObj);
            setTeamTag(team?.tag);
            setTeamTitle(team?.teamName);
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setContentLoader(false);
        }
    }

    const handleTeamTabClick = (e, index) => {
        setTeamPosition(index);
        setSelectedTeam(teams[index]);
        showTeamsDetails(teams[index]);
    }

    const handleTeamTabPositionClick = (e, view) => {
        setTeamTabPosition(view);
    }

    const toggleTitleEdit = () => {
        if(teamTitleEdit) {
            setTeamTitle(selectedTeam?.teamName);
        }
        setTeamTitleEdit(!teamTitleEdit);
    }

    const toggleTagEdit = () => {
        if(teamTagEdit) {
            setTeamTag(selectedTeam?.tag);
        }
        setTeamTagEdit(!teamTagEdit);
    }

    const toggleOwnerEdit = () => {
        if(ownerEdit){
            setOwnerUser(ownerUserBackup);
        }
        setOwnerEdit(!ownerEdit);
    }

    const handleOwnerChange = (e, value) => {
        setOwnerUser(value);
    }

    const handleTeamTagEdit = (e) => {
        let value = DOMPurify.sanitize(e.target.value);
        const validator = /[a-zA-Z]$/;
        if((validator.test(value) || value === '') && value !== null && value.length <= 10) {
            setTeamTag(value.toUpperCase());
        }
    }

    const handleTeamTitleEdit = (e) => {
        let sanitizedText = DOMPurify.sanitize(e.target.value);
        setTeamTitle(sanitizedText);
    }

    const handleEditSubmit = async() => {
        setCursor('wait');
        try {
            let teamObj = selectedTeam;
            teamObj.tag = teamTag;
            teamObj.teamName = teamTitle;
            teamObj.ownerUserId = ownerUser.id;
            await teamService.updateTeam(teamObj);
            setTeamTagEdit(false);
            setTeamTitleEdit(false);
            setOwnerEdit(false);
            await reloadData();
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], { type: mime });
    }

    return (
        <div className={className} style={{cursor: cursor}}>
            {pageLoader ? (
                <div className={`${className}__loader`}>
                    <div className={`${className}__loader__sidebar`}>
                        <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: '12px', marginBottom: '1rem' }} />
                        <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: '8px', marginBottom: '0.75rem' }} />
                        {[...Array(8)].map((_, i) => (
                            <Skeleton key={i} variant="rectangular" width="100%" height={100} sx={{ borderRadius: '12px', marginBottom: '0.75rem' }} />
                        ))}
                    </div>
                    <div className={`${className}__loader__main`}>
                        <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: '16px', marginBottom: '1.5rem' }} />
                        <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: '12px', marginBottom: '1rem' }} />
                        <Skeleton variant="rectangular" width="100%" height={350} sx={{ borderRadius: '16px' }} />
                    </div>
                </div>
            ) : (
                <div className={`${className}__container`}>
                    {/* Sidebar */}
                    {!is0To991 && (
                        <div className={`${className}__sidebar`}>
                            <div className={`${className}__sidebar__header`}>
                                <div className={`${className}__sidebar__header__title`}>
                                    <Teams className={`${className}__sidebar__header__icon`} />
                                    <span>Teams</span>
                                </div>
                                {currentUserOrgPermissions?.allowCreate && (
                                    <Tooltip title="Create New Team" arrow placement="right">
                                        <button 
                                            className={`${className}__sidebar__addBtn`}
                                            onClick={toggleAddTeamForm}
                                        >
                                            <Plus />
                                        </button>
                                    </Tooltip>
                                )}
                            </div>

                            <div className={`${className}__sidebar__search`}>
                                <input
                                    type="text"
                                    placeholder="Search teams..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className={`${className}__sidebar__searchInput`}
                                />
                            </div>

                            <div className={`${className}__sidebar__stats`}>
                                <div className={`${className}__sidebar__stat`}>
                                    <div className={`${className}__sidebar__stat__value`}>{teams.length}</div>
                                    <div className={`${className}__sidebar__stat__label`}>Total Teams</div>
                                </div>
                                <div className={`${className}__sidebar__stat`}>
                                    <div className={`${className}__sidebar__stat__value`}>
                                        {teamUsers.length}
                                    </div>
                                    <div className={`${className}__sidebar__stat__label`}>Total Members</div>
                                </div>
                            </div>

                            <div className={`${className}__sidebar__teams`}>
                                {filteredTeams.length === 0 ? (
                                    <div className={`${className}__sidebar__empty`}>
                                        {searchQuery ? 'No teams found' : 'No teams available'}
                                    </div>
                                ) : (
                                    populateTeams()
                                )}
                            </div>
                        </div>
                    )}

                    {/* Main Content */}
                    <div className={`${className}__main`}>
                        {!selectedTeam || Object.keys(selectedTeam).length === 0 ? (
                            <div className={`${className}__emptyState`}>
                                <Teams className={`${className}__emptyState__icon`} />
                                <h2 className={`${className}__emptyState__title`}>No Team Selected</h2>
                                <p className={`${className}__emptyState__desc`}>
                                    Select a team from the sidebar to view its details
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Team Header */}
                                <div className={`${className}__header`}>
                                    <div className={`${className}__header__left`}>
                                        <div className={`${className}__header__tag`}>{selectedTeam?.tag}</div>
                                        <h1 className={`${className}__header__title`}>
                                            {selectedTeam?.teamName}
                                        </h1>
                                    </div>
                                    <div className={`${className}__header__right`}>
                                        <div className={`${className}__header__stats`}>
                                            <div className={`${className}__header__stat`}>
                                                <People className={`${className}__header__stat__icon`} />
                                                <span className={`${className}__header__stat__value`}>
                                                    {teamUsers.length}
                                                </span>
                                                <span className={`${className}__header__stat__label`}>Members</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Navigation Tabs */}
                                <div className={`${className}__navigation`}>
                                    <div className={`${className}__navigation__tabs`}>
                                        <button 
                                            className={`${className}__navigation__tab ${teamTabPosition === 0 ? 'active' : ''}`}
                                            onClick={() => setTeamTabPosition(0)}
                                        >
                                            <People className={`${className}__navigation__tab__icon`} />
                                            Members
                                        </button>
                                        {selectedTeam.ownerUserId === JSON.parse(activeUserId) && (
                                            <button 
                                                className={`${className}__navigation__tab ${teamTabPosition === 1 ? 'active' : ''}`}
                                                onClick={() => setTeamTabPosition(1)}
                                            >
                                                <Settings className={`${className}__navigation__tab__icon`} />
                                                Settings
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Content Area */}
                                <div className={`${className}__content`}>
                                    {contentLoader ? (
                                        <div className={`${className}__contentLoader`}>
                                            <Skeleton 
                                                variant="rectangular" 
                                                width="100%" 
                                                height={60} 
                                                sx={{ borderRadius: '12px', marginBottom: '1.5rem' }}
                                            />
                                            <Skeleton 
                                                variant="rectangular" 
                                                width="100%" 
                                                height={80} 
                                                sx={{ borderRadius: '12px', marginBottom: '0.75rem' }}
                                            />
                                            <Skeleton 
                                                variant="rectangular" 
                                                width="100%" 
                                                height={80} 
                                                sx={{ borderRadius: '12px', marginBottom: '0.75rem' }}
                                            />
                                            <Skeleton 
                                                variant="rectangular" 
                                                width="100%" 
                                                height={80} 
                                                sx={{ borderRadius: '12px', marginBottom: '0.75rem' }}
                                            />
                                            <Skeleton 
                                                variant="rectangular" 
                                                width="100%" 
                                                height={80} 
                                                sx={{ borderRadius: '12px' }}
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            {
                                                teamTabPosition === 0 && (
                                                    <div className={`${className}__members`}>
                                                        <div className={`${className}__members__header`}>
                                                            <h3 className={`${className}__members__title`}>Team Members</h3>
                                                            {
                                                                permissions.allowAdmin && (
                                                                    <button 
                                                                        className={`${className}__members__addBtn`}
                                                                        onClick={() => toggleAddUsersView('team')}>
                                                                        <Plus className={`${className}__members__addBtn__icon`} />
                                                                        Add Member
                                                                    </button>
                                                                )
                                                            }
                                                        </div>
                                                        <div className={`${className}__members__list`}>
                                                            {
                                                                teamUsers.length === 0 ? (
                                                                    <div className={`${className}__members__empty`}>
                                                                        <People className={`${className}__members__empty__icon`} />
                                                                        <p className={`${className}__members__empty__text`}>
                                                                            No team members yet. Start by adding some members!
                                                                        </p>
                                                                    </div>
                                                                ) : (
                                                                    teamUsers.map((user, i) => (
                                                                        <div key={i} className={`${className}__members__item`}>
                                                                            <UserListRow
                                                                                team={selectedTeam}
                                                                                reloadData={renderData}
                                                                                user={user}
                                                                                parentType='team'/>
                                                                        </div>
                                                                    ))
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                )
                                            }

                                            {teamTabPosition === 1 && (
                                        <div className={`${className}__settings`}>
                                            <div className={`${className}__settings__section`}>
                                                <h3 className={`${className}__settings__title`}>Team Information</h3>
                                                
                                                {/* Tag Edit */}
                                                <div className={`${className}__settings__field`}>
                                                    <label className={`${className}__settings__label`}>Tag</label>
                                                    {teamTagEdit ? (
                                                        <div className={`${className}__settings__edit`}>
                                                            <TextField
                                                                value={teamTag}
                                                                onChange={handleTeamTagEdit}
                                                                variant="outlined"
                                                                size="small"
                                                                className={`${className}__settings__input`}
                                                            />
                                                            <button 
                                                                className={`${className}__settings__saveBtn`}
                                                                onClick={handleEditSubmit}
                                                            >
                                                                <Check />
                                                            </button>
                                                            <button 
                                                                className={`${className}__settings__cancelBtn`}
                                                                onClick={toggleTagEdit}
                                                            >
                                                                <Close />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className={`${className}__settings__display`}>
                                                            <span className={`${className}__settings__value`}>{teamTag}</span>
                                                            {JSON.parse(activeUserId) === ownerUser?.id && (
                                                                <button 
                                                                    className={`${className}__settings__editBtn`}
                                                                    onClick={toggleTagEdit}
                                                                >
                                                                    <Edit />
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Title Edit */}
                                                <div className={`${className}__settings__field`}>
                                                    <label className={`${className}__settings__label`}>Team Name</label>
                                                    {teamTitleEdit ? (
                                                        <div className={`${className}__settings__edit`}>
                                                            <TextField
                                                                value={teamTitle}
                                                                onChange={handleTeamTitleEdit}
                                                                variant="outlined"
                                                                size="small"
                                                                className={`${className}__settings__input`}
                                                            />
                                                            <button 
                                                                className={`${className}__settings__saveBtn`}
                                                                onClick={handleEditSubmit}
                                                            >
                                                                <Check />
                                                            </button>
                                                            <button 
                                                                className={`${className}__settings__cancelBtn`}
                                                                onClick={toggleTitleEdit}
                                                            >
                                                                <Close />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className={`${className}__settings__display`}>
                                                            <span className={`${className}__settings__value`}>{teamTitle}</span>
                                                            {JSON.parse(activeUserId) === ownerUser?.id && (
                                                                <button 
                                                                    className={`${className}__settings__editBtn`}
                                                                    onClick={toggleTitleEdit}
                                                                >
                                                                    <Edit />
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Owner Edit */}
                                                <div className={`${className}__settings__field`}>
                                                    <label className={`${className}__settings__label`}>Team Owner</label>
                                                    {ownerEdit ? (
                                                        <div className={`${className}__settings__edit`}>
                                                            <Autocomplete
                                                                options={orgUsers}
                                                                value={ownerUser}
                                                                className={`${className}__settings__autocomplete`}
                                                                getOptionLabel={(option) => option.fullName + ' : ' + option.userName}
                                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                onChange={handleOwnerChange}
                                                                renderInput={(params) => 
                                                                    <TextField {...params} placeholder='Select Owner' variant='outlined' size="small"/>
                                                                }
                                                            />
                                                            <button 
                                                                className={`${className}__settings__saveBtn`}
                                                                onClick={handleEditSubmit}
                                                            >
                                                                <Check />
                                                            </button>
                                                            <button 
                                                                className={`${className}__settings__cancelBtn`}
                                                                onClick={toggleOwnerEdit}
                                                            >
                                                                <Close />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className={`${className}__settings__display`}>
                                                            <div className={`${className}__settings__owner`}>
                                                                {ownerUser?.photo ? (
                                                                    <img 
                                                                        src={URL.createObjectURL(dataURLtoFile(ownerUser?.photo))}
                                                                        className={`${className}__settings__owner__avatar`} 
                                                                        alt='profile'
                                                                    />
                                                                ) : (
                                                                    <ProfilePicture className={`${className}__settings__owner__avatar`}/>
                                                                )}
                                                                <div className={`${className}__settings__owner__info`}>
                                                                    <span className={`${className}__settings__owner__name`}>
                                                                        {ownerUser?.fullName}
                                                                    </span>
                                                                    <span className={`${className}__settings__owner__username`}>
                                                                        @{ownerUser?.userName}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            {JSON.parse(activeUserId) === ownerUser?.id && (
                                                                <button 
                                                                    className={`${className}__settings__editBtn`}
                                                                    onClick={toggleOwnerEdit}
                                                                >
                                                                    <Edit />
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Danger Zone */}
                                            <div className={`${className}__settings__danger`}>
                                                <h3 className={`${className}__settings__danger__title`}>Danger Zone</h3>
                                                <div className={`${className}__settings__danger__content`}>
                                                    <div className={`${className}__settings__danger__info`}>
                                                        <h4>Delete Team</h4>
                                                        <p>Deleting this team is permanent and cannot be undone.</p>
                                                    </div>
                                                    <button 
                                                        className={`${className}__settings__danger__btn`}
                                                        onClick={handleDeleteClick}
                                                        disabled={cursor === 'wait'}
                                                    >
                                                        Delete Team
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                        </>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Mobile View */}
                    {is0To991 && (
                        <TeamsList
                            teams={teams}
                            toggleAddTeamForm={toggleAddTeamForm}
                            handleTeamTabClick={handleTeamTabClick}
                        />
                    )}
                </div>
            )}

            {showAddTeamForm && (
                <AddTeamForm
                    open={showAddTeamForm}
                    closePopup={toggleAddTeamForm}
                    activeUserId={activeUserId}
                    reloadData={renderData}
                    orgId={currentOrgId}
                />
            )}
            {showDeleteTeamForm && (
                <DeleteTeamForm
                    open={showDeleteTeamForm}
                    closePopup={handleDeleteClick}
                    reloadData={renderData}
                    team={selectedTeam}
                />
            )}
            {showAddUsersView && (
                <AddUsersView
                    open={showAddUsersView}
                    closePopup={toggleAddUsersView}
                    activeUserId={activeUserId}
                    parentType='team'
                    orgId={currentOrgId}
                    team={selectedTeam}
                    reloadData={() => showTeamsDetails(selectedTeam)}
                />
            )}
        </div>
    );
}

